/**
 * FERP Portal - Safety Level Configuration
 *
 * LIFE-CRITICAL: This configuration MUST align with backend SafetyLevel enum.
 * The frontend is DUMB - it only provides UI styling for backend-defined values.
 *
 * Source of truth: graphql_gateway/src/orchestration/mod.rs
 * GraphQL schema: local-schema.graphql
 * Generated types: generated/graphql.ts
 *
 * DO NOT add new safety levels here without backend changes first.
 */

import {
  IconShield,
  IconShieldCheck,
  IconShieldExclamation,
  IconAlertTriangle,
  type Icon,
} from '@tabler/icons-react';
import { SafetyLevel } from '@/types/graphql-compat';

/**
 * UI configuration for each SafetyLevel.
 * Maps backend enum values to display properties.
 *
 * Colors follow severity hierarchy:
 * - STANDARD: gray (normal operations)
 * - ELEVATED: yellow (heightened attention)
 * - CRITICAL: orange (requires immediate attention)
 * - LIFE_CRITICAL: red (patient safety / financial integrity at risk)
 *
 * Priority values for sorting (higher = more critical):
 * - STANDARD: 1
 * - ELEVATED: 2
 * - CRITICAL: 3
 * - LIFE_CRITICAL: 4
 */
export const SAFETY_LEVEL_UI_CONFIG: Record<
  SafetyLevel,
  {
    color: string;
    label: string;
    description: string;
    requiresConfirmation: boolean;
    priority: number;
    icon: Icon;
  }
> = {
  [SafetyLevel.STANDARD]: {
    color: 'gray',
    label: 'Standard',
    description: 'Normal business operations with standard safety requirements',
    requiresConfirmation: false,
    priority: 1,
    icon: IconShieldCheck,
  },
  [SafetyLevel.ELEVATED]: {
    color: 'yellow',
    label: 'Elevated',
    description: 'Operations requiring heightened attention and monitoring',
    requiresConfirmation: false,
    priority: 2,
    icon: IconShield,
  },
  [SafetyLevel.CRITICAL]: {
    color: 'orange',
    label: 'Critical',
    description: 'High-priority operations requiring immediate attention',
    requiresConfirmation: true,
    priority: 3,
    icon: IconShieldExclamation,
  },
  [SafetyLevel.LIFE_CRITICAL]: {
    color: 'red',
    label: 'Life Critical',
    description: 'Operations where failure could result in patient harm or significant financial loss',
    requiresConfirmation: true,
    priority: 4,
    icon: IconAlertTriangle,
  },
};

/**
 * Check if a safety level requires visual emphasis (red border, etc.)
 */
export function isLifeCritical(level: SafetyLevel): boolean {
  return level === SafetyLevel.LIFE_CRITICAL;
}

/**
 * Check if a safety level requires confirmation before actions
 */
export function requiresConfirmation(level: SafetyLevel): boolean {
  return SAFETY_LEVEL_UI_CONFIG[level].requiresConfirmation;
}

/**
 * Get the CSS border style for life-critical items
 */
export function getLifeCriticalBorderStyle(level: SafetyLevel): React.CSSProperties | undefined {
  if (level === SafetyLevel.LIFE_CRITICAL) {
    return { borderLeft: '3px solid var(--mantine-color-red-6)' };
  }
  return undefined;
}

/**
 * Re-export SafetyLevel enum for convenience
 * Components should import from here, or from @/types/graphql-compat
 */
export { SafetyLevel };
